import { config } from 'contants/config';
import LocalStorageHelper from 'utils/common/storage';
import { Order } from 'utils/types/order';
import {
    Oaiq,
    OaiqMeasureOptions,
    OaiqMeasurePayload,
} from 'utils/types/openAiAds';
import { getFinalPrice, toMinorUnits } from './orderValue';

const SCRIPT_ID = 'oaiq-sdk';
const CURRENCY = 'BRL';
const ORDER_CREATED = 'order_created';

export type PurchaseResponse = {
    id?: string;
    subscription?: { id?: string };
};

export type PurchaseParams = {
    order: Order;
    installment?: number;
    response?: PurchaseResponse;
    userId?: string;
};

/**
 * Carrega o Measurement Pixel do ChatGPT Ads.
 * Idempotente: o id do script guarda contra fast refresh, double-effect do
 * React 18 e re-render da flag do Unleash.
 */
export const initOpenAiAds = () => {
    if (typeof window === 'undefined') return;
    if (document.getElementById(SCRIPT_ID)) return;

    // Stub do fornecedor: mantem a semantica de `arguments`, que e como o SDK
    // drena a fila quando carrega. Nao trocar por (...args).
    const stub: Oaiq = function () {
        // eslint-disable-next-line prefer-rest-params
        (stub.q = stub.q || []).push(arguments);
    };

    window.oaiq = window.oaiq || stub;
    window.oaiq('init', { pixelId: config.OPENAI_ADS_PIXEL_ID });

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = config.OPENAI_ADS_SDK_URL;
    document.head.appendChild(script);
};

const getStoredUserId = () => {
    try {
        return LocalStorageHelper.get<string>('userId');
    } catch {
        return undefined;
    }
};

/**
 * Chave de deduplicacao do evento.
 *
 * O ideal e um id vindo do backend, que hoje nao e exposto no client. Enquanto
 * isso, o fallback agrupa por usuario + plano + minuto: cobre double-fire da
 * mesma compra (re-render, double submit), mas NAO deduplica atravessando a
 * virada do minuto, e nao pode ser casado com a Conversions API server-side.
 */
export const buildEventId = ({ order, response, userId }: PurchaseParams) => {
    const backendId = response?.subscription?.id || response?.id;

    if (backendId) return `order_${backendId}`;

    const minuteBucket = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/\D/g, '');

    return `order_${userId || 'anon'}_${
        order?.plan?.slug || 'plan'
    }_${minuteBucket}`;
};

/**
 * Conversao de compra.
 *
 * O valor enviado e o total do contrato, o mesmo `final_value` do GA4
 * (Config/Trackers/datalayer.ts), para pixel e GA4 nunca divergirem.
 *
 * PIX dispara junto, na geracao do QR Code, igual ao `purchase` de hoje - ou
 * seja, PIX gerado e nao pago tambem conta. A correcao definitiva e a
 * Conversions API server-side reportando PIX pago.
 */
export const trackOpenAiPurchase = (params: PurchaseParams) => {
    const { order, installment } = params;

    // Sem window.oaiq o pixel nao carregou (flag off, adblock, CSP): no-op
    // silencioso, para nao arriscar o caminho critico da compra.
    if (typeof window === 'undefined' || !window.oaiq || !order) return;

    try {
        const amountInReais =
            getFinalPrice(order, installment) || order.plan?.price;

        if (!amountInReais) return;

        const payload: OaiqMeasurePayload = {
            type: 'contents',
            amount: toMinorUnits(amountInReais),
            currency: CURRENCY,
        };

        const options: OaiqMeasureOptions = {
            event_id: buildEventId({
                ...params,
                userId: params.userId || getStoredUserId(),
            }),
        };

        window.oaiq('measure', ORDER_CREATED, payload, options);
    } catch (error) {
        console.error(error);
    }
};
